import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { TextareaInput } from "@/components/ui/Field";
import { useTimesheetFlowLayers } from "@/features/timesheetFlow/timesheetFlowLayers";
import {
  openDokumentBillingAnswerParts,
  respondDokumentBillingQuestion,
  type DokumentBillingQuestion,
} from "@/lib/dokumentBilling";

export function InvoiceBillingAnswerDialog({
  open,
  onClose,
  dokumentId,
  questions,
  invoiceLabel,
  onSaved,
  onError,
}: {
  open: boolean;
  onClose: () => void;
  dokumentId: number;
  questions: DokumentBillingQuestion[];
  invoiceLabel?: string;
  onSaved: () => void;
  onError: (message: string) => void;
}) {
  const layers = useTimesheetFlowLayers();
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [savedKeys, setSavedKeys] = useState<string[]>([]);

  useEffect(() => {
    if (!open) {
      setDrafts({});
      setSavingKey(null);
      setSavedKeys([]);
    }
  }, [open]);

  const parts = useMemo(() => {
    const saved = new Set(savedKeys);
    return openDokumentBillingAnswerParts(questions).filter((part) => !saved.has(part.key));
  }, [questions, savedKeys]);

  const hasDrafts = Object.values(drafts).some((value) => value.trim() !== "");

  const savePart = async (key: string, questionId: number, itemId: number | null) => {
    const text = (drafts[key] || "").trim();
    if (!text) {
      onError("Bitte eine Antwort eingeben.");
      return;
    }
    setSavingKey(key);
    try {
      await respondDokumentBillingQuestion(
        dokumentId,
        questionId,
        itemId != null
          ? { items: [{ item_id: itemId, response_text: text }] }
          : { response_text: text },
      );
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setSavedKeys((prev) => (prev.includes(key) ? prev : [...prev, key]));
      onSaved();
    } catch (err: unknown) {
      onError(err instanceof Error ? err.message : "Antwort konnte nicht gespeichert werden.");
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      dirty={hasDrafts}
      size="3xl"
      zIndexClass={layers.deep}
      fullScreenMobile
      title="Rückfragen beantworten"
      description={
        invoiceLabel ||
        "Antworten zu den Rückfragen der verknüpften Rechnung."
      }
      footer={
        <Button type="button" variant="secondary" onClick={onClose}>
          Schließen
        </Button>
      }
    >
      {parts.length === 0 ? (
        <div className="rounded-ios-lg border border-status-green/30 bg-status-green/5 px-4 py-6 text-center">
          <p className="text-base font-medium text-label">Alle Rückfragen sind beantwortet.</p>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm leading-relaxed text-label-secondary">
            Beantworte jede Frage der verknüpften Rechnung einzeln. Gespeicherte Antworten
            verschwinden aus der Liste.
          </p>
          {parts.map((part) => {
            const draft = drafts[part.key] || "";
            const busy = savingKey === part.key;
            return (
              <article
                key={part.key}
                className="rounded-ios-lg border border-amber-500/30 bg-amber-500/5 p-4 sm:p-5"
              >
                <p className="text-sm font-semibold leading-snug text-label">{part.positionLabel}</p>
                <p className="mt-2 whitespace-pre-wrap text-sm text-label">{part.questionText}</p>
                <TextareaInput
                  className="mt-3"
                  label="Deine Antwort"
                  rows={4}
                  value={draft}
                  onChange={(e) =>
                    setDrafts((prev) => ({ ...prev, [part.key]: e.target.value }))
                  }
                  placeholder="Antwort eingeben…"
                />
                <div className="mt-3 flex justify-end">
                  <Button
                    type="button"
                    disabled={busy || !draft.trim()}
                    onClick={() => void savePart(part.key, part.questionId, part.itemId)}
                  >
                    {busy ? "Speichern…" : "Antwort speichern"}
                  </Button>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </Dialog>
  );
}
